import { Booking } from '../types';

/**
 * Booking status arrives from the API in either case ("COMPLETED" from
 * DynamoDB, "completed" from the admin status updates), so every comparison
 * here normalises before matching.
 */
export const normalizeStatus = (booking: Pick<Booking, 'status'> | null | undefined): string =>
  String(booking?.status || '').trim().toLowerCase();

export const isCompletedBooking = (booking: Pick<Booking, 'status'> | null | undefined): boolean =>
  normalizeStatus(booking) === 'completed';

/**
 * Splits the bookings list into the work that is still outstanding and the
 * work that is finished, preserving the order of the input list so the
 * user's chosen sort still applies inside each section.
 */
export const splitBookingsByCompletion = <T extends Pick<Booking, 'status'>>(
  bookings: T[]
): { active: T[]; completed: T[] } => {
  const active: T[] = [];
  const completed: T[] = [];

  (bookings || []).forEach(booking => {
    if (isCompletedBooking(booking)) {
      completed.push(booking);
    } else {
      active.push(booking);
    }
  });

  return { active, completed };
};

export interface Page<T> {
  items: T[];
  /** 1-based, clamped into range */
  currentPage: number;
  totalPages: number;
  total: number;
  /** 0-based index of the first item on the page */
  startIndex: number;
  /** 1-based index of the last item on the page, for "showing X-Y of Z" */
  endIndex: number;
}

/**
 * Client-side pagination over an already-loaded list.
 *
 * `page` is clamped rather than trusted: deleting a booking or flipping one to
 * completed can shrink a section while the user sits on its last page.
 */
export const paginate = <T>(items: T[], page: number, pageSize: number): Page<T> => {
  const list = items || [];
  const total = list.length;
  const size = Math.max(1, Math.floor(pageSize) || 1);
  const totalPages = Math.max(1, Math.ceil(total / size));
  const currentPage = Math.min(Math.max(1, Math.floor(page) || 1), totalPages);
  const startIndex = (currentPage - 1) * size;
  const pageItems = list.slice(startIndex, startIndex + size);

  return {
    items: pageItems,
    currentPage,
    totalPages,
    total,
    startIndex,
    endIndex: startIndex + pageItems.length
  };
};
